import type { PortfolioReport } from '@interfaces/portfolioReport';
import { Box } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import { buildAuditPortfolioSnapshot } from '../../utils/snapshot/buildAuditPortfolioSnapshot';
import { buildReportDisplayMetadata } from '../../utils/snapshot/buildReportDisplayMetadata';
import { BulkRebalanceAuditPortfolioGrid } from '../auditGrid/auditPortfolioGrid';

export type BulkRebalanceWeightDiffTableProps = {
  audit?: OptimizerAuditV2Record;
  tickerMap?: Record<string, string>;
  report?: PortfolioReport;
  applyReportHoldings?: boolean;
};

/** Audit holding grid for a rebalanced portfolio (dollar view). */
export const BulkRebalanceWeightDiffTable = ({
  audit,
  tickerMap = {},
  report,
  applyReportHoldings = false,
}: BulkRebalanceWeightDiffTableProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance', 'accountTypes', 'components']);

  const snapshot = useMemo(
    () =>
      buildAuditPortfolioSnapshot(
        audit,
        tickerMap,
        report
          ? buildReportDisplayMetadata(translateBackendString, report, t)
          : undefined,
        { applyReportHoldings },
      ),
    [applyReportHoldings, audit, report, t, tickerMap],
  );

  if (snapshot.diffRows.length === 0) {
    return null;
  }

  return (
    <Box data-testid='bulk-rebalance-weight-diff-table'>
      <BulkRebalanceAuditPortfolioGrid snapshot={snapshot} />
    </Box>
  );
};
